import React, { useState, useEffect } from 'react'
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Alert,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem as SelectMenuItem,
  Grid
} from '@mui/material'
import {
  Visibility,
  Edit,
  Undo,
  Cancel as CancelIcon,
  MoreVert,
  CheckCircle,
  Schedule,
  Error as ErrorIcon,
  Refresh
} from '@mui/icons-material'
import { useSnackbar } from 'notistack'
import { format, isAfter, parseISO } from 'date-fns'
import fileMovementService from '../../../services/fileMovementService'

const MovementList = ({ fileId, onEdit, onView }) => {
  const { enqueueSnackbar } = useSnackbar()
  
  const [loading, setLoading] = useState(false)
  const [movements, setMovements] = useState([])
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [totalCount, setTotalCount] = useState(0)
  const [anchorEl, setAnchorEl] = useState(null)
  const [selectedMovement, setSelectedMovement] = useState(null)
  const [openReturnDialog, setOpenReturnDialog] = useState(false)
  const [openCancelDialog, setOpenCancelDialog] = useState(false)
  const [returnData, setReturnData] = useState({
    condition: 'GOOD',
    conditionRemarks: ''
  })
  const [cancelReason, setCancelReason] = useState('')
  
  // Filters
  const [statusFilter, setStatusFilter] = useState('ALL')
  const [typeFilter, setTypeFilter] = useState('ALL')

  useEffect(() => {
    loadMovements()
  }, [fileId, page, rowsPerPage, statusFilter, typeFilter])

  const loadMovements = async () => {
    setLoading(true)
    try {
      const params = {
        page: page + 1,
        limit: rowsPerPage,
        status: statusFilter !== 'ALL' ? statusFilter : undefined,
        movementType: typeFilter !== 'ALL' ? typeFilter : undefined
      }

      if (fileId) {
        params.fileId = fileId
      }

      const response = await fileMovementService.getMovements(params)
      
      if (response.data.success) {
        setMovements(response.data.data)
        setTotalCount(response.data.pagination?.total || response.data.data.length)
      }
    } catch (error) {
      enqueueSnackbar('Failed to load movements', { variant: 'error' })
    } finally {
      setLoading(false)
    }
  }

  const handleMenuOpen = (event, movement) => {
    setAnchorEl(event.currentTarget)
    setSelectedMovement(movement)
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
    setSelectedMovement(null)
  }

  const handleReturn = async () => {
    try {
      await fileMovementService.returnMovement(selectedMovement._id, returnData)
      enqueueSnackbar('File returned successfully', { variant: 'success' })
      setOpenReturnDialog(false)
      setReturnData({ condition: 'GOOD', conditionRemarks: '' })
      loadMovements()
    } catch (error) {
      enqueueSnackbar('Failed to return file', { variant: 'error' })
    }
  }

  const handleCancel = async () => {
    try {
      await fileMovementService.cancelMovement(selectedMovement._id, cancelReason)
      enqueueSnackbar('Movement cancelled successfully', { variant: 'success' })
      setOpenCancelDialog(false)
      setCancelReason('')
      loadMovements()
    } catch (error) {
      enqueueSnackbar('Failed to cancel movement', { variant: 'error' })
    }
  }

  const getStatusChip = (status, movement) => {
    // Determine actual status based on movement state
    let actualStatus = status
    if (movement.isReturned && movement.condition === 'CANCELLED') {
      actualStatus = 'CANCELLED'
    } else if (movement.isReturned) {
      actualStatus = 'RETURNED'
    } else if (!movement.isReturned) {
      actualStatus = 'ISSUED'
    }

    const config = {
      PENDING: { color: 'warning', icon: <Schedule fontSize="small" /> },
      ISSUED: { color: 'info', icon: <Schedule fontSize="small" /> },
      IN_TRANSIT: { color: 'info', icon: <Schedule fontSize="small" /> },
      DELIVERED: { color: 'primary', icon: <CheckCircle fontSize="small" /> },
      RETURNED: { color: 'success', icon: <CheckCircle fontSize="small" /> },
      CANCELLED: { color: 'error', icon: <CancelIcon fontSize="small" /> },
      OVERDUE: { color: 'error', icon: <ErrorIcon fontSize="small" /> }
    }
    
    const { color, icon } = config[actualStatus] || { color: 'default', icon: null }
    
    return (
      <Chip
        label={actualStatus.replace('_', ' ')}
        color={color}
        size="small"
        icon={icon}
      />
    )
  }

  const getPriorityChip = (priority) => {
    const config = {
      LOW: { color: 'default' },
      NORMAL: { color: 'primary' },
      HIGH: { color: 'warning' },
      URGENT: { color: 'error' }
    }
    
    return (
      <Chip
        label={priority}
        color={config[priority]?.color || 'default'}
        size="small"
        variant="outlined"
      />
    )
  }

  const isOverdue = (movement) => {
    if (movement.isReturned) {
      return false
    }
    return movement.expectedReturnDate && isAfter(new Date(), parseISO(movement.expectedReturnDate))
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" fontWeight={600}>
          File Movements ({totalCount})
        </Typography>
        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={loadMovements}
        >
          Refresh
        </Button>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <SelectMenuItem value="ALL">All Status</SelectMenuItem>
                <SelectMenuItem value="ISSUED">Issued</SelectMenuItem>
                <SelectMenuItem value="RETURNED">Returned</SelectMenuItem>
                <SelectMenuItem value="CANCELLED">Cancelled</SelectMenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Movement Type</InputLabel>
              <Select
                value={typeFilter}
                label="Movement Type"
                onChange={(e) => setTypeFilter(e.target.value)}
              >
                <SelectMenuItem value="ALL">All Types</SelectMenuItem>
                <SelectMenuItem value="DEPARTMENT_TRANSFER">Department Transfer</SelectMenuItem>
                <SelectMenuItem value="EXTERNAL_REQUEST">External Request</SelectMenuItem>
                <SelectMenuItem value="AUDIT_REVIEW">Audit Review</SelectMenuItem>
                <SelectMenuItem value="LEGAL_REVIEW">Legal Review</SelectMenuItem>
                <SelectMenuItem value="INSURANCE_CLAIM">Insurance Claim</SelectMenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {/* Movements Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>File Details</TableCell>
              <TableCell>Request Info</TableCell>
              <TableCell>Movement Type</TableCell>
              <TableCell>From → To</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Priority</TableCell>
              <TableCell>Expected Return</TableCell>
              <TableCell>Created</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {movements.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  <Alert severity="info">
                    No movements found
                  </Alert>
                </TableCell>
              </TableRow>
            ) : (
              movements.map((movement) => (
                <TableRow 
                  key={movement._id} 
                  hover
                  sx={{
                    bgcolor: isOverdue(movement) ? 'error.50' : 'inherit'
                  }}
                >
                  <TableCell>
                    <Box>
                      <Typography variant="body2" fontWeight={600}>
                        {movement.mrdFile?.fileNumber}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {movement.mrdFile?.patientName}
                      </Typography>
                    </Box>
                  </TableCell>
                  
                  <TableCell>
                    {movement.relatedRequest ? (
                      <Box>
                        <Typography variant="body2" fontWeight={600} color="primary">
                          #{movement.relatedRequest.requestNumber}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {movement.relatedRequest.requestType} - {movement.relatedRequest.requestedBy}
                        </Typography>
                      </Box>
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        Manual Movement
                      </Typography>
                    )}
                  </TableCell>
                  
                  <TableCell>
                    <Typography variant="body2">
                      {movement.movementType?.replace(/_/g, ' ')}
                    </Typography>
                  </TableCell>
                  
                  <TableCell>
                    <Box>
                      <Typography variant="body2">
                        {movement.fromLocation || 'MRD'}
                      </Typography>
                      <Typography variant="caption" color="primary">
                        ↓
                      </Typography>
                      <Typography variant="body2" color="primary">
                        {movement.toLocation}
                      </Typography>
                    </Box>
                  </TableCell>
                  
                  <TableCell>
                    {getStatusChip(isOverdue(movement) ? 'OVERDUE' : movement.status, movement)}
                  </TableCell>
                  
                  <TableCell>
                    {getPriorityChip(movement.priority)}
                  </TableCell>
                  
                  <TableCell>
                    <Typography variant="body2">
                      {movement.expectedReturnDate 
                        ? format(parseISO(movement.expectedReturnDate), 'MMM dd, yyyy')
                        : '-'
                      }
                    </Typography>
                    {isOverdue(movement) && (
                      <Typography variant="caption" color="error">
                        Overdue
                      </Typography>
                    )}
                  </TableCell>
                  
                  <TableCell>
                    <Typography variant="body2">
                      {format(parseISO(movement.createdAt), 'MMM dd, yyyy')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {movement.issuedBy?.name}
                    </Typography>
                  </TableCell>
                  
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Tooltip title="View Details">
                        <IconButton
                          size="small"
                          onClick={() => onView ? onView(movement) : null}
                        >
                          <Visibility fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      
                      {!movement.isReturned && (
                        <Tooltip title="Return File">
                          <IconButton
                            size="small"
                            color="success"
                            onClick={() => {
                              setSelectedMovement(movement)
                              setOpenReturnDialog(true)
                            }}
                          >
                            <Undo fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      
                      <IconButton
                        size="small"
                        onClick={(e) => handleMenuOpen(e, movement)}
                      >
                        <MoreVert fontSize="small" />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10))
            setPage(0)
          }}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      </TableContainer>

      {/* Context Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => {
          onEdit && onEdit(selectedMovement)
          handleMenuClose()
        }}>
          <ListItemIcon>
            <Edit fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>
        
        {selectedMovement && !selectedMovement.isReturned && (
          <MenuItem onClick={() => {
            setOpenCancelDialog(true)
            handleMenuClose()
          }}>
            <ListItemIcon>
              <CancelIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Cancel</ListItemText>
          </MenuItem>
        )}
      </Menu>

      {/* Return Dialog */}
      <Dialog open={openReturnDialog} onClose={() => setOpenReturnDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Return File</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>File Condition</InputLabel>
              <Select
                value={returnData.condition}
                label="File Condition"
                onChange={(e) => setReturnData(prev => ({ ...prev, condition: e.target.value }))}
              >
                <SelectMenuItem value="GOOD">Good</SelectMenuItem>
                <SelectMenuItem value="DAMAGED">Damaged</SelectMenuItem>
                <SelectMenuItem value="INCOMPLETE">Incomplete</SelectMenuItem>
              </Select>
            </FormControl>
            
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Condition Remarks"
              value={returnData.conditionRemarks}
              onChange={(e) => setReturnData(prev => ({ ...prev, conditionRemarks: e.target.value }))}
              placeholder="Enter condition remarks..."
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenReturnDialog(false)}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleReturn}>
            Return File
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cancel Dialog */}
      <Dialog open={openCancelDialog} onClose={() => setOpenCancelDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Cancel Movement</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Cancellation Reason"
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              placeholder="Enter reason for cancellation..."
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCancelDialog(false)}>
            Cancel
          </Button>
          <Button variant="contained" color="error" onClick={handleCancel}>
            Cancel Movement
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default MovementList